import HttpBackend from 'i18next-http-backend';

import i18next, { use } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { supportedLocales } from './supported-locales';
import { locales } from '@i18n/locales';
import { convertAkTagsToPascal } from '@/custom-hooks/useTrans';

function deepClone(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
}

export const loadI18n = (initialLocale: string, callThisWhenDone: () => void): void => {
    use(HttpBackend)
        .use(initReactI18next)
        .use({
            type: 'postProcessor',
            name: 'akTagProcessor',
            process(value: string) {
                return convertAkTagsToPascal(value);
            },
        })
        .init(
            {
                lng: initialLocale,
                load: 'currentOnly',
                supportedLngs: supportedLocales,
                fallbackLng: [initialLocale],
                debug: false,
                preload: [initialLocale],
                interpolation: { escapeValue: false },
                resources: deepClone(locales),
                ns: Object.keys(locales['en-US']),
                defaultNS: 'common',
                react: { useSuspense: true },
            },
            () => {
                const data = i18next.store.data;

                function walk(obj: any): any {
                    if (typeof obj === 'string') return convertAkTagsToPascal(obj);

                    if (Array.isArray(obj)) return obj.map(walk);

                    if (obj && typeof obj === 'object') {
                        return Object.fromEntries(
                            Object.entries(obj).map(([k, v]) => [k, walk(v)])
                        );
                    }

                    return obj;
                }

                for (const lng of Object.keys(data)) {
                    for (const ns of Object.keys(data[lng])) {
                        const transformed = walk(data[lng][ns]);
                        i18next.addResourceBundle(lng, ns, transformed, true, true);
                    }
                }

                callThisWhenDone();
            }
        );
};

type Vals = (typeof locales)[keyof typeof locales];

type ValueAtPath<K, T> = K extends `${infer K1}${'.'}${infer K2}`
    ? K1 extends keyof T
        ? ValueAtPath<K2, T[K1]>
        : never
    : K extends keyof T
      ? T[K]
      : never;

export type TranslationAtKey<K> = K extends keyof Vals
    ? Vals[K]
    : K extends `${infer K1}${':'}${infer K2}`
      ? K1 extends keyof Vals
          ? ValueAtPath<K2, Vals[K1]>
          : never
      : ValueAtPath<K, Vals['common']>;

export type Namespace = keyof Vals;
